import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowLeft, ChevronDown, Check, Info } from 'lucide-react';
import { markers as allMarkers, BRANDS } from '../utils/data';
import { getUserCollection } from '../utils/userCollection';
import { findCollectionGaps, getBrandCoverageStats, splitOwnedUnowned } from '../utils/collectionGap';
import { usesOldNewCodes } from '../utils/color';
import ExpandCollectionIntroModal, { hasSeenExpandIntro } from '../components/ExpandCollectionIntroModal';
import { useAuth } from '../contexts/AuthContext';
import { markExpandCollectionSeen } from '../services/profileService';

// ─── Hue Coverage Strip ───────────────────────────────────────────────────────

const HUE_BUCKETS = [
  { label: 'Red',    hue: 0,   color: '#ef4444' },
  { label: 'Orange', hue: 30,  color: '#f97316' },
  { label: 'Yellow', hue: 60,  color: '#eab308' },
  { label: 'Lime',   hue: 90,  color: '#84cc16' },
  { label: 'Green',  hue: 120, color: '#22c55e' },
  { label: 'Teal',   hue: 150, color: '#14b8a6' },
  { label: 'Cyan',   hue: 180, color: '#06b6d4' },
  { label: 'Sky',    hue: 210, color: '#3b82f6' },
  { label: 'Blue',   hue: 240, color: '#6366f1' },
  { label: 'Purple', hue: 270, color: '#a855f7' },
  { label: 'Pink',   hue: 300, color: '#ec4899' },
  { label: 'Rose',   hue: 330, color: '#f43f5e' },
];

function HueCoverageStrip({ owned }) {
  const coverage = useMemo(() => {
    return HUE_BUCKETS.map(bucket => {
      const count = owned.filter(m => {
        if (!m.hex) return false;
        try {
          const hex = m.hex.replace('#', '');
          const r = parseInt(hex.slice(0, 2), 16) / 255;
          const g = parseInt(hex.slice(2, 4), 16) / 255;
          const b = parseInt(hex.slice(4, 6), 16) / 255;
          const max = Math.max(r, g, b), min = Math.min(r, g, b);
          if (max - min < 0.12) return false;
          let h = 0;
          if (max === r) h = ((g - b) / (max - min) + (g < b ? 6 : 0)) * 60;
          else if (max === g) h = ((b - r) / (max - min) + 2) * 60;
          else h = ((r - g) / (max - min) + 4) * 60;
          const diff = Math.abs(((h - bucket.hue) + 180) % 360 - 180);
          return diff <= 15;
        } catch { return false; }
      }).length;
      return { ...bucket, count };
    });
  }, [owned]);

  return (
    <div>
      <label className="block text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400 mb-2">
        Your hue coverage
      </label>
      <div className="flex rounded-xl overflow-hidden h-4">
        {coverage.map(bucket => (
          <div
            key={bucket.label}
            className="flex-1 transition-opacity"
            style={{
              backgroundColor: bucket.color,
              opacity: bucket.count > 0 ? Math.min(0.3 + bucket.count * 0.15, 1) : 0.08,
            }}
            title={`${bucket.label}: ${bucket.count} marker${bucket.count !== 1 ? 's' : ''}`}
          />
        ))}
      </div>
      <div className="flex mt-1">
        {coverage.map(bucket => (
          <div key={bucket.label} className="flex-1 text-center">
            <span className="text-[9px] text-slate-400">{bucket.count > 0 ? bucket.count : ''}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Suggestion Card ──────────────────────────────────────────────────────────

function getTextColor(hex) {
  const clean = (hex || '').replace('#', '');
  if (clean.length < 6) return '#1e293b';
  const r = parseInt(clean.substr(0, 2), 16);
  const g = parseInt(clean.substr(2, 2), 16);
  const b = parseInt(clean.substr(4, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#1e293b' : '#ffffff';
}

function SuggestionCard({ suggestion, rank }) {
  const { marker, gain, reason } = suggestion;
  const codes = usesOldNewCodes(marker.brand)
    ? [marker.code_new, marker.code_old].filter(Boolean)
    : [marker.code].filter(Boolean);

  const textColor = getTextColor(marker.hex);
  const coverageScore = Math.min(100, Math.round((gain / 60) * 100));
  const scoreColor = coverageScore >= 70 ? 'text-emerald-600' : coverageScore >= 40 ? 'text-amber-600' : 'text-slate-500';

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-3 flex items-center gap-3">
      <div className="text-xs font-bold text-slate-300 w-4 text-center shrink-0">{rank}</div>

      <div
        className="w-16 h-16 rounded-xl shrink-0 shadow-sm relative"
        style={{ backgroundColor: marker.hex }}
      >
        <div className="absolute bottom-2 left-2 flex flex-col items-start gap-0.5">
          {codes.map((c, i) => (
            <span
              key={i}
              className="text-[11px] font-mono font-semibold leading-tight block"
              style={{ color: textColor }}
            >
              {c}
            </span>
          ))}
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900 truncate">{marker.name || codes[0]}</p>
        <p className="text-xs text-slate-400 truncate">{marker.brand}</p>
        <p className="text-xs text-violet-600 font-medium mt-0.5 truncate">{reason}</p>
      </div>

      <div className="text-right shrink-0">
        <p className={`text-sm font-bold ${scoreColor}`}>{coverageScore}%</p>
        <p className="text-[10px] text-slate-400">coverage</p>
      </div>
    </div>
  );
}

// ─── Brand Selector (matches CustomSelect from HexFinderPage) ─────────────────

function GapBrandSelector({ stats, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = stats.find(s => s.brand === value);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-1.5 px-3 py-2 rounded-xl text-xs font-medium cursor-pointer bg-white/80 backdrop-blur-sm border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-rose-pastel-300"
      >
        <span className="truncate">{value || 'Select a brand…'}</span>
        <div className="flex items-center gap-2 shrink-0">
          {selected && <span className="text-slate-400">{selected.owned}/{selected.total}</span>}
          <ChevronDown size={13} className={`text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {open && (
        <div className="absolute left-0 right-0 mt-2 bg-white/95 backdrop-blur-md rounded-xl shadow-soft-xl border border-slate-100 py-2 z-50 animate-fadeIn max-h-60 overflow-y-auto">
          {stats.length === 0 && (
            <p className="text-xs text-slate-400 px-4 py-3">No brands with partial ownership found.</p>
          )}
          {stats.map(s => {
            const pct = Math.round((s.owned / s.total) * 100);
            return (
              <button
                key={s.brand}
                type="button"
                onClick={() => { onChange(s.brand); setOpen(false); }}
                className={`w-full px-4 py-1.5 text-left text-xs flex items-center justify-between hover:bg-slate-50 transition-colors ${value === s.brand ? 'text-violet-600 font-semibold' : 'text-slate-700'}`}
              >
                <span className="truncate">{s.brand}</span>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-violet-400 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-slate-400 w-10 text-right">{s.owned}/{s.total}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Count Select (matches CountSelect from RandomPalettePage) ────────────────

function CountSelect({ value, onChange, max }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const options = Array.from({ length: Math.min(max, 20) }, (_, i) => i + 1);

  return (
    <div className="relative min-w-[80px]" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-1.5 px-3 py-2 rounded-xl text-xs font-medium cursor-pointer bg-white/80 backdrop-blur-sm border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-rose-pastel-300"
      >
        {value}
        <ChevronDown size={13} className={`text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 mt-2 w-24 bg-white/95 backdrop-blur-md rounded-xl shadow-soft-xl border border-slate-100 py-2 z-50 animate-fadeIn max-h-48 overflow-y-auto">
          {options.map(n => (
            <button
              key={n}
              type="button"
              onClick={() => { onChange(n); setOpen(false); }}
              className={`w-full px-4 py-1.5 text-left text-xs flex items-center justify-between hover:bg-slate-50 transition-colors ${value === n ? 'text-violet-600 font-semibold' : 'text-slate-700'}`}
            >
              {n}
              {value === n && <Check size={12} className="text-violet-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ExpandCollectionPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showIntro, setShowIntro] = useState(!hasSeenExpandIntro());

  // Dismiss the collection nudge banner on first visit (covers tools modal path)
  useEffect(() => {
    const localSeen = localStorage.getItem('markerSync_expandCollectionSeen') === 'true';
    if (!localSeen) {
      localStorage.setItem('markerSync_expandCollectionSeen', 'true');
      if (user) markExpandCollectionSeen(user.uid);
    }
  }, [user]);

  const [collection] = useState(() => getUserCollection());
  const [selectedBrand, setSelectedBrand] = useState('');
  const [budget, setBudget] = useState(5);
  const [suggestions, setSuggestions] = useState([]);
  const [isAnalysing, setIsAnalysing] = useState(false);
  const [hasAnalysed, setHasAnalysed] = useState(false);

  const brandStats = useMemo(
    () => getBrandCoverageStats(collection, allMarkers, BRANDS),
    [collection]
  );

  useEffect(() => {
    if (brandStats.length > 0 && !selectedBrand) {
      setSelectedBrand(brandStats[0].brand);
    }
  }, [brandStats, selectedBrand]);

  const { owned, unowned } = useMemo(() => {
    if (!selectedBrand) return { owned: [], unowned: [] };
    return splitOwnedUnowned(collection, allMarkers, selectedBrand);
  }, [selectedBrand, collection]);

  const selectedStats = brandStats.find(s => s.brand === selectedBrand);
  const maxBudget = Math.min(20, unowned.length);

  const handleAnalyse = useCallback(() => {
    if (!selectedBrand || owned.length === 0) return;
    setIsAnalysing(true);
    setTimeout(() => {
      const results = findCollectionGaps(owned, unowned, budget);
      setSuggestions(results);
      setHasAnalysed(true);
      setIsAnalysing(false);
    }, 30);
  }, [selectedBrand, owned, unowned, budget]);

  useEffect(() => {
    if (hasAnalysed) handleAnalyse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand, budget]);

  const isComplete = selectedStats && selectedStats.unownedCount === 0;
  const tooFew = owned.length < 3 && !isComplete;

  return (
    <div className="w-full mx-auto sm:px-6 lg:px-8 space-y-8">
      <ExpandCollectionIntroModal isOpen={showIntro} onClose={() => setShowIntro(false)} />

      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/collection')}
          className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-600 transition-colors mb-4"
        >
          <ArrowLeft size={14} />
          Back to collection
        </button>
        <h2 className="text-2xl md:text-4xl font-display font-bold heading-display mb-3 animate-pulse bg-gradient-to-r from-violet-500 to-purple-500">
          Expand your collection <Sparkles size={26} className="inline-block pb-1 text-violet-500" />
        </h2>
        <p className="text-base md:text-md text-elegant max-w-2xl">
          Find which markers would add the most new hues to your collection — covering color ranges you're currently missing.
        </p>
      </div>

      {collection.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-slate-500 text-sm mb-2">Your collection is empty.</p>
          <button onClick={() => navigate('/collection')} className="text-violet-600 text-sm font-medium hover:underline">
            Set up your collection first →
          </button>
        </div>
      ) : brandStats.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-slate-500 text-sm">
            No brands with partial ownership found. Add some markers to your collection to get started.
          </p>
        </div>
      ) : (
        <>
          {/* Controls */}
          <div className="flex items-end gap-3">

            {/* Brand selector */}
            <div className="flex-1 space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400">
                Brand
              </label>
              <GapBrandSelector
                stats={brandStats}
                value={selectedBrand}
                onChange={(b) => { setSelectedBrand(b); setHasAnalysed(false); setSuggestions([]); }}
              />
            </div>

            {/* Suggestion count */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400">
                Suggestions
              </label>
              <CountSelect value={budget} onChange={setBudget} max={maxBudget} />
            </div>
          </div>

          {/* Hue strip */}
          {owned.length > 0 && !tooFew && !isComplete && (
            <HueCoverageStrip owned={owned} />
          )}

          {/* Edge cases */}
          {tooFew && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
              <Info size={14} className="text-amber-500 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700 leading-relaxed">
                You own {owned.length} marker{owned.length !== 1 ? 's' : ''} from {selectedBrand}. Add a few more to get meaningful suggestions (minimum 3).
              </p>
            </div>
          )}

          {isComplete && (
            <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-xl p-3">
              <Sparkles size={14} className="text-emerald-500 shrink-0 mt-0.5" />
              <p className="text-xs text-emerald-700 leading-relaxed">
                Your {selectedBrand} collection is complete — you own all the markers from this brand!
              </p>
            </div>
          )}

          {/* Analyse button — only before first run */}
          {!tooFew && !isComplete && !hasAnalysed && (
            <div className="flex justify-center">
              <button
                onClick={handleAnalyse}
                disabled={isAnalysing || !selectedBrand}
                className="btn btn-primary-green flex items-center gap-2"
              >
                {isAnalysing ? 'Analysing…' : 'Analyse gaps'}
              </button>
            </div>
          )}
          {isAnalysing && hasAnalysed && (
            <p className="text-xs text-slate-400">Updating…</p>
          )}

          {/* Results */}
          {hasAnalysed && suggestions.length > 0 && (
            <div>
              <div className="mb-3">
                <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400">
                  Top {suggestions.length} suggestion{suggestions.length !== 1 ? 's' : ''}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">Coverage score ↑ = more new territory</p>
              </div>
              <div className="space-y-3">
                {suggestions.map((s, i) => (
                  <SuggestionCard key={`${s.marker.brand}-${s.marker.code}-${i}`} suggestion={s} rank={i + 1} />
                ))}
              </div>

              {budget >= maxBudget && unowned.length > suggestions.length && (
                <p className="text-xs text-slate-400 text-center mt-4">
                  {unowned.length - suggestions.length} more unowned {selectedBrand} marker{unowned.length - suggestions.length !== 1 ? 's' : ''} not shown.
                </p>
              )}
            </div>
          )}

          {hasAnalysed && suggestions.length === 0 && !isComplete && (
            <p className="text-slate-500 text-sm">No suggestions available for this selection.</p>
          )}
        </>
      )}
    </div>
  );
}
